import { localPoint } from "@visx/event";
import type React from "react";
import { useCallback } from "react";
import ChartHelpers from "helpers/ChartHelpers";
import useAreaChart from "../AreaChartProvider/useAreaChartStore";
import useDrawingActions from "../DrawingProvider/useDrawingActions";
import useShowPointsTooltip from "../hooks/useShowPointsTooltip";
import type { Points } from "../TooltipProvider";
import useTooltip from "../TooltipProvider/useTooltipStore";
import useXAxis from "../XAxisProvider/useXAxisStore/useXAxisStore";
import useXAxisActions from "../XAxisProvider/useXAxisActions";

type OnMouseMoveEvent = React.TouchEvent<SVGElement> | React.MouseEvent<SVGElement>;

type AreaChartProps = {
  children: React.ReactNode;
  renderTooltip?: (data: Points<unknown>) => React.ReactNode;
};

function Svg(props: AreaChartProps) {
  const { height, width } = useAreaChart();
  const { hideTooltip } = useTooltip();
  const { xAxis } = useXAxis();
  const { getXScale } = useXAxisActions();
  const { handleClearCoordinates } = useDrawingActions();
  const showPointsTooltip = useShowPointsTooltip();

  const onMouseMove = useCallback(
    (e: OnMouseMoveEvent) => {
      const point = localPoint(e) || { x: 0, y: 0 };
      const closestX = ChartHelpers.bisector(xAxis.data, getXScale(), point.x, (d) => d);
      if (!closestX) return;

      showPointsTooltip(closestX.datum, closestX.index);
    },
    [getXScale, showPointsTooltip, xAxis.data]
  );

  const onMouseOut = useCallback(() => {
    hideTooltip();
    handleClearCoordinates();
  }, [handleClearCoordinates, hideTooltip]);

  return (
    <svg
      width={width}
      height={height}
      onTouchStart={onMouseMove}
      onTouchMove={onMouseMove}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseOut}
    >
      {props.children}
    </svg>
  );
}

export default Svg;
